import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  Input,
  Table,
  Tag,
  Modal,
  Form,
  Select,
  Switch,
  Space,
  Tooltip,
  Typography,
  message,
  Popconfirm,
  Divider,
  Empty,
  Segmented,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";
import PagesHeader from "../../components/Common/PagesHeader";
import DebouncedCourseSelect from "../../components/Common/DebouncedCourseSelect";

const { Text, Paragraph } = Typography;

const COLLECTION_TYPES = [
  { label: "Professional Certificate", value: "professional-certificate" },
  { label: "Specialization", value: "specialization" },
  { label: "Learning Path", value: "learning-path" },
  { label: "Exam Prep Bundle", value: "exam-prep-bundle" },
  { label: "Microcredential", value: "microcredential" },
];

const TYPE_COLORS = {
  "professional-certificate": "purple",
  specialization: "blue",
  "learning-path": "cyan",
  "exam-prep-bundle": "gold",
  microcredential: "geekblue",
};

const DEFAULT_PLATFORMS = [
  "Coursera",
  "Udemy",
  "edX",
  "Codecademy",
  "Pluralsight",
  "DataCamp",
  "LinkedIn Learning",
  "Udacity",
  "FutureLearn",
  "Google Cloud Skills",
  "AWS Skill Builder",
  "Microsoft Learn",
];

const CourseCollectionsList = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [courseAddMode, setCourseAddMode] = useState("catalog");

  const [form] = Form.useForm();
  const selectedPlatform = Form.useWatch("platform", form);
  const [bundleCourses, setBundleCourses] = useState([]);
  const [newStubTitle, setNewStubTitle] = useState("");
  const [newStubPlatform, setNewStubPlatform] = useState("");
  const [newStubUrl, setNewStubUrl] = useState("");
  const [availableTags, setAvailableTags] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);

  // Fetch collections
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get("/v3/admin/course-collections?limit=100");
      const data = response.data?.data || [];
      setCollections(data);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to fetch course collections");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tags for multi-select
  const fetchTags = async () => {
    try {
      const response = await axiosHttp.get("/v3/admin/tags");
      const resData = response?.data;
      let list = [];
      if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData?.results)) {
        list = resData.results;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      setAvailableTags(list);
    } catch (error) {
      console.warn("Could not fetch tags", error);
      setAvailableTags([]);
    }
  };

  // Fetch registered platforms
  const fetchPlatforms = async () => {
    try {
      const response = await axiosHttp.get("/v1/admin/platforms?size=100");
      const resData = response?.data;
      let list = [];
      if (Array.isArray(resData?.results?.data)) {
        list = resData.results.data;
      } else if (Array.isArray(resData?.data?.data)) {
        list = resData.data.data;
      } else if (Array.isArray(resData?.data)) {
        list = resData.data;
      } else if (Array.isArray(resData?.results)) {
        list = resData.results;
      } else if (Array.isArray(resData)) {
        list = resData;
      }
      setAvailablePlatforms(list);
    } catch (error) {
      console.warn("Could not fetch platforms", error);
      setAvailablePlatforms([]);
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchTags();
    fetchPlatforms();
  }, []);

  const platformOptions = useMemo(() => {
    const list = Array.isArray(availablePlatforms) ? availablePlatforms : [];
    const dynamicNames = list
      .map((p) => (typeof p === "object" ? p?.name || p?.title : p))
      .filter(Boolean);
    const combined = Array.from(new Set([...dynamicNames, ...DEFAULT_PLATFORMS])).sort((a, b) =>
      a.localeCompare(b)
    );
    return combined.map((name) => ({ label: name, value: name }));
  }, [availablePlatforms]);

  const filteredCollections = useMemo(() => {
    return collections.filter((c) => {
      const matchesSearch =
        !searchText ||
        (c.name && c.name.toLowerCase().includes(searchText.toLowerCase())) ||
        (c.provider && c.provider.toLowerCase().includes(searchText.toLowerCase())) ||
        (c.platform && c.platform.toLowerCase().includes(searchText.toLowerCase()));

      const matchesType = typeFilter === "all" || c.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [collections, searchText, typeFilter]);

  const openCreateModal = () => {
    setEditingCollection(null);
    form.resetFields();
    form.setFieldsValue({
      type: "professional-certificate",
      isActive: true,
    });
    setBundleCourses([]);
    setNewStubTitle("");
    setNewStubPlatform("");
    setNewStubUrl("");
    setCourseAddMode("catalog");
    setIsModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingCollection(record);
    setCourseAddMode("catalog");
    form.resetFields();
    form.setFieldsValue({
      name: record.name,
      slug: record.slug,
      type: record.type || "professional-certificate",
      provider: record.provider || "",
      platform: record.platform || "",
      duration: record.duration || "",
      description: record.description || "",
      url: record.url || "",
      imageUrl: record.imageUrl || "",
      isActive: record.isActive !== false,
      tags: (record.tags || []).map((t) => (typeof t === "object" ? t._id : t)),
    });

    const courses = (record.courses || []).map((entry, idx) => {
      const c = entry.course || {};
      const isCourseObj = typeof c === "object" && c !== null;
      const cId = isCourseObj ? (c._id || c.id) : (typeof entry.course === "string" ? entry.course : null);
      const isStub = !!entry.stub || c.status === "stub";
      const stubData = entry.stub || {};

      let title = isStub
        ? (stubData.title || c.title || "Stub Course")
        : (c.title || c.name || (typeof entry.course === "string" ? entry.course : "Course"));
      let platform = isStub
        ? (stubData.platform || c.platform || c.merchant || "")
        : (c.merchant || c.platform || c.provider || "");

      return {
        key: `existing-${cId || idx}`,
        courseId: isStub ? null : cId,
        stub: isStub ? (entry.stub || { title, platform }) : undefined,
        title,
        platform,
        status: isStub ? "stub" : "active",
        order: entry.order !== undefined ? entry.order : idx,
      };
    });
    setBundleCourses(courses);

    // If any courses are unpopulated (title is just an ID or missing), fetch details asynchronously
    courses.forEach((item, index) => {
      if (item.status === "active" && item.courseId && (!item.title || item.title === item.courseId)) {
        axiosHttp
          .get(`/v1/course/${item.courseId}`)
          .then((res) => {
            const courseData = res?.data?.results || res?.data?.data || res?.data;
            if (courseData) {
              setBundleCourses((prev) =>
                prev.map((c, i) =>
                  i === index
                    ? {
                        ...c,
                        title: courseData.title || courseData.name || c.title,
                        platform: courseData.merchant || courseData.provider || courseData.platform || c.platform,
                      }
                    : c
                )
              );
            }
          })
          .catch(() => {});
      }
    });

    setIsModalVisible(true);
  };

  // Add existing course from selector
  const handleAddExistingCourse = (courseIdVal, courseOption) => {
    const targetId = Array.isArray(courseIdVal)
      ? courseIdVal[courseIdVal.length - 1]
      : typeof courseIdVal === "object"
      ? courseIdVal?.value
      : courseIdVal;
    if (!targetId) return;

    if (bundleCourses.some((item) => item.courseId === targetId)) {
      return message.warning("This course is already added to the bundle.");
    }

    const opt = Array.isArray(courseOption)
      ? courseOption[courseOption.length - 1]
      : courseOption;

    const raw = opt?.raw || {};
    let cleanTitle = raw.title || raw.name || opt?.title;
    if (!cleanTitle && typeof opt?.label === "string") {
      cleanTitle = opt.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
    }
    if (!cleanTitle) {
      cleanTitle = targetId;
    }

    const platform = raw.merchant || raw.provider || raw.platform || opt?.merchant || "";

    setBundleCourses((prev) => [
      ...prev,
      {
        key: `course-${targetId}-${Date.now()}`,
        courseId: targetId,
        title: cleanTitle,
        platform,
        status: "active",
        order: prev.length,
      },
    ]);
    message.success("Course added to bundle!");
  };

  // Add new stub course
  const handleAddNewStub = () => {
    if (!newStubTitle.trim()) {
      return message.warning("Please provide a course title for the stub.");
    }
    const resolvedPlatform = (newStubPlatform || selectedPlatform || "").trim();
    const stubObj = {
      key: `stub-${Date.now()}`,
      stub: {
        title: newStubTitle.trim(),
        platform: resolvedPlatform,
        url: newStubUrl.trim(),
      },
      title: newStubTitle.trim(),
      platform: resolvedPlatform,
      status: "stub",
      order: bundleCourses.length,
    };
    setBundleCourses((prev) => [...prev, stubObj]);
    setNewStubTitle("");
    setNewStubPlatform("");
    setNewStubUrl("");
    message.success("New stub course added to collection!");
  };

  const handleRemoveCourse = (indexToRemove) => {
    setBundleCourses((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        ...values,
        courses: bundleCourses.map((c, idx) => ({
          ...(c.courseId ? { courseId: c.courseId } : {}),
          ...(c.stub ? { stub: c.stub } : {}),
          order: idx,
        })),
      };

      if (editingCollection?._id) {
        await axiosHttp.put(`/v3/admin/course-collections/${editingCollection._id}`, payload);
        message.success("Course collection updated successfully!");
      } else {
        await axiosHttp.post("/v3/admin/course-collections", payload);
        message.success("Course collection created successfully!");
      }

      setIsModalVisible(false);
      fetchCollections();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || "Failed to save course collection");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosHttp.delete(`/v3/admin/course-collections/${id}`);
      message.success("Collection deleted successfully!");
      fetchCollections();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to delete collection");
    }
  };

  const columns = [
    {
      title: "Collection Name",
      dataIndex: "name",
      key: "name",
      minWidth: 260,
      render: (name, record) => (
        <div style={{ maxWidth: 360 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", lineHeight: 1.3 }}>
            {name}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace", marginTop: 2 }}>
            /{record.slug}
          </div>
          {record.description && (
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                marginTop: 4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={record.description}
            >
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 140,
      render: (type) => (
        <Tag
          color={TYPE_COLORS[type] || "default"}
          style={{
            fontWeight: 600,
            textTransform: "capitalize",
            fontSize: 11,
            borderRadius: 4,
            padding: "2px 8px",
            margin: 0,
          }}
        >
          {type ? type.replace(/-/g, " ") : "Program"}
        </Tag>
      ),
    },
    {
      title: "Provider / Platform",
      key: "provider",
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#334155" }}>
            {record.provider || "—"}
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{record.platform || "—"}</div>
        </div>
      ),
    },
    {
      title: "Duration & Bundle",
      key: "duration",
      width: 145,
      render: (_, record) => {
        const count = record.courses?.length || 0;
        const stubCount = (record.courses || []).filter(
          (c) => c.course?.status === "stub" || c.stub
        ).length;
        return (
          <div>
            <div style={{ fontSize: 12, color: "#0f172a", display: "flex", alignItems: "center", gap: 4 }}>
              <ClockCircleOutlined style={{ color: "#6366f1", fontSize: 12 }} />
              <span>{record.duration || "Self-paced"}</span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
              <BookOutlined style={{ fontSize: 12 }} />
              <span>{count} {count === 1 ? "course" : "courses"}</span>
              {stubCount > 0 && (
                <Tag color="warning" style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>
                  {stubCount} stub{stubCount > 1 ? "s" : ""}
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 85,
      align: "center",
      render: (isActive) => (
        <Tag
          color={isActive !== false ? "success" : "default"}
          style={{ fontSize: 11, borderRadius: 4, margin: 0 }}
        >
          {isActive !== false ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size={2}>
          {record.url && (
            <Tooltip title="Open external URL">
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => window.open(record.url, "_blank")}
                style={{ padding: "0 4px" }}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit collection">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: "#6366f1" }} />}
              onClick={() => openEditModal(record)}
              style={{ padding: "0 4px" }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Collection"
            description="Are you sure you want to delete this course collection?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ padding: "0 4px" }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Page title="Course Collections">
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <PagesHeader
            pageTitle="Course Collections & Programs"
            homePageUrl="/"
            homePageText="Dashboard"
            activePageText="Course Collections"
            floatRight={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
                style={{ background: "#6366f1", borderColor: "#6366f1", borderRadius: 6, fontWeight: 600 }}
              >
                Create Collection
              </Button>
            }
          />

          {/* Subtitle description */}
          <div style={{ color: "#64748b", fontSize: 13, marginTop: -15, marginBottom: 18 }}>
            Curate multi-course certificate programs, specializations, learning paths, and exam prep bundles for skill and topic pages.
          </div>

          {/* Filters Bar */}
          <Card
            bodyStyle={{ padding: "10px 16px" }}
            style={{ marginBottom: 16, borderRadius: 8, borderColor: "#e2e8f0" }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12 }}>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 12, flex: 1, minWidth: 280 }}>
                <Input
                  placeholder="Search by collection name, provider, platform..."
                  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ minWidth: 260, maxWidth: 380, borderRadius: 6 }}
                  allowClear
                />
                <Select
                  value={typeFilter}
                  onChange={setTypeFilter}
                  style={{ width: 210 }}
                  options={[{ label: "All Program Types", value: "all" }, ...COLLECTION_TYPES]}
                />
                {(searchText || typeFilter !== "all") && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setTypeFilter("all");
                    }}
                    style={{ color: "#6366f1", padding: 0 }}
                  >
                    Reset filters
                  </Button>
                )}
              </div>
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                <span style={{ color: "#64748b", fontSize: 13 }}>
                  Showing <b>{filteredCollections.length}</b> {filteredCollections.length === 1 ? "collection" : "collections"}
                </span>
                <Tooltip title="Refresh collections">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={fetchCollections}
                    size="small"
                  />
                </Tooltip>
              </div>
            </div>
          </Card>

          {/* Table */}
          <Card
            bodyStyle={{ padding: 0 }}
            style={{ borderRadius: 8, borderColor: "#e2e8f0", overflow: "hidden", boxShadow: "0 2px 4px -1px rgba(0,0,0,0.03)" }}
          >
            <Table
              columns={columns}
              dataSource={filteredCollections}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 920 }}
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} collections`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div>
                        <div style={{ fontWeight: 600, color: "#475569", fontSize: 14 }}>
                          {searchText || typeFilter !== "all"
                            ? "No matching course collections found"
                            : "No course collections created yet"}
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                          {searchText || typeFilter !== "all"
                            ? "Try adjusting your search criteria or resetting filters."
                            : "Build structured certificates, specializations, and course bundles to display on skill pages."}
                        </div>
                      </div>
                    }
                  >
                    {!searchText && typeFilter === "all" && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        size="small"
                        style={{ marginTop: 8, borderRadius: 6, background: "#6366f1", borderColor: "#6366f1" }}
                      >
                        Create First Collection
                      </Button>
                    )}
                  </Empty>
                ),
              }}
            />
          </Card>

          {/* Create / Edit Modal (Clean Tabbed Layout) */}
          <Modal
            title={
              <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
                {editingCollection ? "✏️ Edit Course Collection" : "✨ Create New Course Collection"}
              </span>
            }
            centered
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            onOk={handleFormSubmit}
            confirmLoading={saving}
            okText={editingCollection ? "Save Changes" : "Create Collection"}
            width={720}
            style={{ maxWidth: "calc(100vw - 40px)" }}
            destroyOnClose
            bodyStyle={{ padding: "8px 24px 20px" }}
          >
            <Form form={form} layout="vertical">
              <Tabs
                defaultActiveKey="general"
                items={[
                  {
                    key: "general",
                    label: (
                      <span>
                        <BookOutlined style={{ marginRight: 6 }} />
                        General Details
                      </span>
                    ),
                    children: (
                      <div style={{ paddingTop: 6, minHeight: 460 }}>
                        <Form.Item
                          label="Collection Name"
                          name="name"
                          rules={[{ required: true, message: "Collection name is required" }]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input placeholder="e.g. IBM Java Developer Professional Certificate" />
                        </Form.Item>

                        <div className="row">
                          <div className="col-md-6">
                            <Form.Item label="Program Type" name="type" style={{ marginBottom: 12 }}>
                              <Select options={COLLECTION_TYPES} />
                            </Form.Item>
                          </div>
                          <div className="col-md-6">
                            <Form.Item label="Hosting Platform" name="platform" style={{ marginBottom: 12 }}>
                              <Select
                                showSearch
                                allowClear
                                placeholder="Select platform"
                                options={platformOptions}
                                filterOption={(input, option) =>
                                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                              />
                            </Form.Item>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <Form.Item label="Provider / Institution" name="provider" style={{ marginBottom: 12 }}>
                              <Input placeholder="e.g. IBM, Google, Georgia Tech" />
                            </Form.Item>
                          </div>
                          <div className="col-md-6">
                            <Form.Item label="Estimated Duration" name="duration" style={{ marginBottom: 12 }}>
                              <Input placeholder="e.g. ~3 months, 40 hours" />
                            </Form.Item>
                          </div>
                        </div>

                        <Form.Item label="External Program URL" name="url" style={{ marginBottom: 12 }}>
                          <Input placeholder="https://coursera.org/professional-certificates/..." />
                        </Form.Item>

                        <div className="row">
                          <div className="col-md-8">
                            <Form.Item label="Linked Skill Tags" name="tags" style={{ marginBottom: 12 }}>
                              <Select
                                mode="multiple"
                                placeholder="Select skill tags..."
                                options={(Array.isArray(availableTags) ? availableTags : []).map((t) => ({
                                  label: t.name,
                                  value: t._id,
                                }))}
                                filterOption={(input, option) =>
                                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                              />
                            </Form.Item>
                          </div>
                          <div className="col-md-4">
                            <Form.Item label="Status" name="isActive" valuePropName="checked" style={{ marginBottom: 12 }}>
                              <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
                            </Form.Item>
                          </div>
                        </div>

                        <Form.Item label="Description / Learning Objective" name="description" style={{ marginBottom: 0 }}>
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            placeholder="Summarize what this certificate program validates, key skills learned, or practical projects built..."
                          />
                        </Form.Item>
                      </div>
                    ),
                  },
                  {
                    key: "courses",
                    label: (
                      <span>
                        <PlusOutlined style={{ marginRight: 6 }} />
                        Courses in Bundle ({bundleCourses.length})
                      </span>
                    ),
                    children: (
                      <div style={{ paddingTop: 6, minHeight: 460 }}>
                        {/* Switcher & Adder */}
                        <div
                          style={{
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            padding: "8px 12px",
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#475569",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Add Course to Bundle:
                              </span>
                              {selectedPlatform ? (
                                <Tag color="blue" style={{ fontSize: 11, margin: 0, borderRadius: 4 }}>
                                  Platform: {selectedPlatform}
                                </Tag>
                              ) : (
                                <Tag style={{ fontSize: 11, margin: 0, color: "#64748b", borderRadius: 4 }}>
                                  All Platforms
                                </Tag>
                              )}
                            </div>
                            <Segmented
                              size="small"
                              value={courseAddMode}
                              onChange={setCourseAddMode}
                              options={[
                                { label: "Catalog Search", value: "catalog", icon: <SearchOutlined /> },
                                { label: "Create Stub", value: "stub", icon: <PlusOutlined /> },
                              ]}
                            />
                          </div>

                          {courseAddMode === "catalog" ? (
                            <DebouncedCourseSelect
                              placeholder={
                                selectedPlatform
                                  ? `Search ${selectedPlatform} courses in catalog...`
                                  : "Search by course title or merchant to add..."
                              }
                              merchant={selectedPlatform}
                              mode="single"
                              onChange={handleAddExistingCourse}
                              value={null}
                            />
                          ) : (
                            <div>
                              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                                <Input
                                  placeholder="Course Title (e.g. Capstone Project)"
                                  value={newStubTitle}
                                  onChange={(e) => setNewStubTitle(e.target.value)}
                                  style={{ flex: 2 }}
                                />
                                <Select
                                  showSearch
                                  allowClear
                                  placeholder="Platform"
                                  value={newStubPlatform || selectedPlatform || undefined}
                                  onChange={(val) => setNewStubPlatform(val || "")}
                                  options={platformOptions}
                                  style={{ flex: 1 }}
                                  filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                  }
                                />
                              </div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <Input
                                  placeholder="Website URL (optional)"
                                  value={newStubUrl}
                                  onChange={(e) => setNewStubUrl(e.target.value)}
                                  style={{ flex: 1 }}
                                />
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  onClick={handleAddNewStub}
                                  style={{ background: "#6366f1", borderColor: "#6366f1" }}
                                >
                                  Add Stub Course
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Added Courses List */}
                        {bundleCourses.length === 0 ? (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "20px 12px",
                              color: "#94a3b8",
                              fontSize: 13,
                              background: "#fafafa",
                              border: "1px dashed #e2e8f0",
                              borderRadius: 8,
                            }}
                          >
                            No courses added yet. Search your catalog or add a stub course above.
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 5,
                              maxHeight: 350,
                              overflowY: "auto",
                              paddingRight: 4,
                            }}
                          >
                            {bundleCourses.map((c, idx) => (
                              <div
                                key={c.key || idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "5px 10px",
                                  background: "#ffffff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: 6,
                                  fontSize: 13,
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                                  <span style={{ fontWeight: 700, color: "#94a3b8", width: 22, fontSize: 12 }}>
                                    {idx + 1}.
                                  </span>
                                  <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    <span style={{ fontWeight: 600, color: "#0f172a" }}>{c.title}</span>
                                    {c.platform && (
                                      <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>
                                        ({c.platform})
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                  {c.status === "stub" ? (
                                    <Tag color="gold" style={{ fontSize: 11, padding: "0 6px", margin: 0 }}>
                                      Stub
                                    </Tag>
                                  ) : (
                                    <Tag color="green" style={{ fontSize: 11, padding: "0 6px", margin: 0 }}>
                                      <CheckCircleOutlined style={{ marginRight: 3 }} /> Catalog
                                    </Tag>
                                  )}
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemoveCourse(idx)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </Form>
          </Modal>
        </div>
      </div>
    </Page>
  );
};

export default CourseCollectionsList;
